// Frontend types for project-based multi-video architecture
// These should match the backend types in EQ/backend/src/types/index.ts

export enum ContentType {
  ShortFilm = 'ShortFilm',
  Educational = 'Educational',
  Marketing = 'Marketing'
}

export enum ToneType {
  Serious = 'Serious',
  Fun = 'Fun'
}

export enum DesiredTimeframe {
  ThirtySeconds = '30s',
  OneMinute = '1min',
  TwoMinutes = '2mins',
  FiveMinutes = '5mins',
  FifteenMinutes = '15mins',
  More = 'more'
}

export enum TargetMarket {
  Progressive = 'Progressive',
  Neutral = 'neutral',
  Conservative = 'conservative'
}

export enum ToneNew {
  Poetic = 'Poetic',
  Inspirational = 'Inspirational',
  Reverent = 'Reverent'
}

export enum FormatType {
  BiographicMontage = 'Biographic Montage',
  NewReport = 'New Report',
  ExplainerBurst = 'Explainer Burst',
  OpinionSnap = 'Opinion Snap',
  Commercial = 'Commercial',
  TeaserTrailer = 'Teaser Trailer'
}

export enum VideoStatus {
  Included = 'Included',
  Excluded = 'Excluded'
}

export enum ProjectStatus {
  New = 'New',
  UploadComplete = 'Upload Complete',
  Processing = 'Processing',
  Failed = 'Failed',
  ProcessingComplete = 'Processing Complete',
  UploadedToSocials = 'Uploaded to Socials'
}

export enum PublicationStatus {
  Pending = 'Pending',
  Published = 'Published',
  Failed = 'Failed',
  Scheduled = 'Scheduled'
}

export enum PublicationPlatform {
  YouTube = 'YouTube',
  TikTok = 'TikTok',
  Instagram = 'Instagram',
  Facebook = 'Facebook'
}

export interface VideoMetadata {
  duration: number;
  resolution: {
    width: number;
    height: number;
  };
  format: string;
  fileSize?: number;
}

export interface CompilationMetadata {
  totalDuration: number;
  videoCount: number;
  optimalSequence: string[];
  narrativeFlow: {
    introduction: boolean;
    mainContent: boolean;
    conclusion: boolean;
    hooks: number;
  };
  crossVideoAnalysis: {
    visualConsistency: number;
    audioBalance: number;
    pacingFlow: number;
  };
}

export interface UsageSegment {
  startTime: number;
  endTime: number;
  purpose: 'introduction' | 'main_content' | 'transition' | 'conclusion' | 'hook';
  sequencePosition: number;
}

export interface Video {
  id: string;
  project_id: string;
  user_id: string;
  original_filename: string;
  storage_bucket: string;
  original_url: string;
  sequence_order: number;
  status: VideoStatus;
  created_at: string;
  metadata: VideoMetadata;
  usage_segments: UsageSegment[];
  individual_analysis: {
    sceneBoundaries: number[];
    contentClassification: string[];
    audioCharacteristics: any;
  };
}

export interface Publication {
  id: string;
  project_id: string;
  platform: PublicationPlatform;
  status: PublicationStatus;
  platform_video_id?: string;
  platform_url?: string;
  publish_date?: string;
  scheduled_date?: string;
  title: string;
  description: string;
  tags: string[];
  metrics: {
    views?: number;
    likes?: number;
    comments?: number;
    engagementRate?: number;
  };
  created_at: string;
  updated_at: string;
}

export interface Project {
  id: string;
  user_id: string;
  name: string;
  description: string;
  content_type_id: string;
  tone_old?: ToneType;
  tone_new?: ToneNew;
  desired_timeframe?: DesiredTimeframe;
  target_market?: TargetMarket;
  format_type?: FormatType;
  status: ProjectStatus;
  compiled_video_url?: string;
  preview_url?: string;
  created_at: string;
  updated_at: string;
  processing_started_at?: string;
  processing_completed_at?: string;
  compilation_metadata: CompilationMetadata;
  optimization_rules: any[];
  compilation_actions: any[];
  performance_metrics: any;
  processing_warnings: string[];
  applied_rule_ids: string[];
  publication_platforms: PublicationPlatform[];
  videos?: Video[];
  publications?: Publication[];
  content_types?: {
    name: string;
    description: string;
    icon: string;
  };
}

export interface ProjectCreateData {
  name: string;
  description?: string;
  content_type_id: string;
  tone_new: ToneNew;
  desired_timeframe: DesiredTimeframe;
  target_market: TargetMarket;
  format_type: FormatType;
}

export interface ProjectUpdateData {
  name?: string;
  description?: string;
  content_type_id?: string;
  tone_new?: ToneNew;
  desired_timeframe?: DesiredTimeframe;
  target_market?: TargetMarket;
  format_type?: FormatType;
}

export interface PublishData {
  platforms: PublicationPlatform[];
  customMetadata?: {
    [platform: string]: {
      title?: string;
      description?: string;
      tags?: string[];
    };
  };
} 